import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Camera, ArrowLeft, Search, AlertCircle, Trash2, Copy, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 2000;
const POLL_GIVE_UP_MS = 20 * 60 * 1000;

const FALLBACK_CONSENT =
  'I agree that Memouraa may use my selfie to search this event\'s photos for my face. ' +
  'My selfie is processed in memory only for this search and is not stored. ' +
  'Faces in this event\'s photos are detected only while my search runs and are not stored. ' +
  'I get a private results link that expires after 30 days; I can delete it at any time with Forget me.';

export default function FindMePage() {
  const { auraKey } = useParams();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [capturedUrl, setCapturedUrl] = useState(null);
  const [searching, setSearching] = useState(false);
  const [progress, setProgress] = useState(null);
  const [matches, setMatches] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [consent, setConsent] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/${auraKey}/find-me/consent`, { withCredentials: true })
      .then(({ data }) => setInfo(data))
      .catch((err) => {
        if (err.response?.status === 401) setError('This event is password protected. Open the portal and enter the password first.');
        setInfo({ enabled: false });
      });
  }, [auraKey]);

  useEffect(() => () => {
    if (stream) stream.getTracks().forEach(t => t.stop());
    if (capturedUrl) URL.revokeObjectURL(capturedUrl);
  }, [stream, capturedUrl]);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  const startCamera = async () => {
    try {
      setError('');
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 720, height: 720 },
        audio: false,
      });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.play();
      }
    } catch {
      setError('Camera access denied. You can upload a selfie photo instead.');
    }
  };

  const captureFromCamera = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedBlob(blob);
        setCapturedUrl(URL.createObjectURL(blob));
        if (stream) stream.getTracks().forEach(t => t.stop());
        setStream(null);
      }
    }, 'image/jpeg', 0.85);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCapturedBlob(file);
    setCapturedUrl(URL.createObjectURL(file));
  };

  const finish = (data) => {
    setSearching(false);
    setProgress(null);
    setMatches(data.matches || []);
    setShareToken(data.token || null);
    // The selfie is no longer needed in the browser either.
    setCapturedBlob(null);
  };

  const poll = (searchId, startedAt) => {
    pollRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/portal/${auraKey}/find-me/${searchId}`, { withCredentials: true });
        if (data.status === 'done' || data.status === 'forgotten') return finish(data);
        if (data.status === 'failed') {
          setSearching(false);
          setProgress(null);
          return setError(data.error || 'Search failed. Please try again.');
        }
        if (data.status === 'running') setProgress({ done: data.done, total: data.total });
        if (Date.now() - startedAt > POLL_GIVE_UP_MS) {
          setSearching(false);
          setProgress(null);
          return setError('The search is taking too long. Please try again later.');
        }
        poll(searchId, startedAt);
      } catch (err) {
        setSearching(false);
        setProgress(null);
        setError(err.response?.data?.detail || 'Search failed. Please try again.');
      }
    }, POLL_MS);
  };

  const handleSearch = async () => {
    if (!capturedBlob || !consent) return;
    setSearching(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', capturedBlob, 'selfie.jpg');
      formData.append('consent', 'true');
      const { data } = await axios.post(`${API_URL}/api/portal/${auraKey}/find-me`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setProgress({ done: 0, total: 0 });
      poll(data.search_id, Date.now());
    } catch (err) {
      setSearching(false);
      setError(err.response?.data?.detail || 'Search failed. Please try again with a clearer selfie.');
    }
  };

  const reset = () => {
    if (pollRef.current) clearTimeout(pollRef.current);
    setCapturedBlob(null);
    setCapturedUrl(null);
    setMatches(null);
    setShareToken(null);
    setSearching(false);
    setProgress(null);
    setError('');
  };

  const handleForgetMe = async () => {
    if (!shareToken) {
      reset();
      return;
    }
    if (!window.confirm('This deletes your private results link now. Continue?')) return;
    try {
      await axios.delete(`${API_URL}/api/my-moments/${shareToken}`);
      reset();
      alert('Done. Your results link is deleted. No face data of yours was ever stored.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete your results link. Please try again.');
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/my-moments/${shareToken}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const consentText = info?.consent_text || FALLBACK_CONSENT;
  // On, but the face engine is not installed/working on this server: say so
  // before the guest takes a selfie (the search would only fail with a 503).
  const unavailable = !!info && info.enabled !== false && info.available === false;
  const disabled = (!!info && info.enabled === false) || unavailable;

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />
      <div className="border-b border-champagne/10 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to={`/portal/${auraKey}`} className="text-text-muted hover:text-champagne transition-colors flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to portal
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-champagne" />
            <span className="font-serif text-text-light">Find Me</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {disabled && (
          <div className="aura-card rounded-2xl p-8 text-center" data-testid={unavailable ? 'find-me-unavailable' : 'find-me-off'}>
            <h1 className="text-2xl font-serif font-medium text-text-light mb-2">
              {unavailable ? "Find me isn't available right now" : 'Find me is off'}
            </h1>
            <p className="text-text-muted text-sm">
              {unavailable
                ? 'Photo search is temporarily unavailable for this event. Please try again later, or browse the gallery.'
                : (error || 'The host has not switched on Find me for this event.')}
            </p>
          </div>
        )}

        {!disabled && !matches && !capturedUrl && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="aura-card rounded-2xl p-8 text-center">
            <Camera className="w-12 h-12 text-champagne mx-auto mb-4" />
            <h1 className="text-2xl font-serif font-medium text-text-light mb-2">Find your photos</h1>
            <p className="text-text-muted mb-6 text-sm leading-relaxed">
              Take a quick selfie and we'll look for you in this event's photos.<br />
              Your selfie is used for this one search and never stored.
            </p>

            {!stream ? (
              <div className="flex flex-col gap-3">
                <button onClick={startCamera} className="btn-champagne flex items-center justify-center gap-2">
                  <Camera className="w-5 h-5" /> Use Camera
                </button>
                <button onClick={() => fileInputRef.current?.click()} className="btn-outline flex items-center justify-center gap-2">
                  Upload Selfie
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="user"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl overflow-hidden bg-black aspect-square max-w-sm mx-auto">
                  <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
                </div>
                <button onClick={captureFromCamera} className="btn-champagne">
                  Capture
                </button>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <p className="mt-6 text-xs text-text-muted/70 leading-relaxed" data-testid="find-me-footer">
              Your selfie is processed in memory for this search only and then discarded; it is never saved.
              Faces in the event's photos are detected only while your search runs, and no face data of
              anyone is stored. We keep only your private results link (which photos matched), for 30 days.
              "Forget me" deletes it at any time.
            </p>
          </motion.div>
        )}

        <canvas ref={canvasRef} className="hidden" />

        {!disabled && capturedUrl && !matches && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="aura-card rounded-2xl p-6 text-center">
            <img src={capturedUrl} alt="Your selfie" className="rounded-xl mx-auto max-w-xs mb-6" />
            <label className="flex items-start gap-3 text-left text-sm text-text-muted max-w-md mx-auto mb-5 cursor-pointer" data-testid="find-me-consent">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={searching}
                className="mt-1 w-4 h-4 accent-champagne shrink-0"
              />
              <span>{consentText}</span>
            </label>
            <div className="flex flex-col gap-3 max-w-xs mx-auto">
              <button onClick={handleSearch} disabled={searching || !consent || !capturedBlob} className="btn-champagne flex items-center justify-center gap-2 disabled:opacity-50">
                <Search className="w-5 h-5" /> {searching ? 'Searching...' : 'Find My Photos'}
              </button>
              <button onClick={reset} className="btn-outline">{searching ? 'Cancel' : 'Retake'}</button>
            </div>
            {searching && progress && (
              <p className="text-text-muted text-xs mt-4" aria-live="polite">
                {progress.total ? `Checked ${progress.done} of ${progress.total} photos…` : 'Starting your search…'}
              </p>
            )}
            {error && (
              <p className="text-red-400 text-sm mt-4">{error}</p>
            )}
          </motion.div>
        )}

        {matches && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="aura-card rounded-2xl p-6">
              <h2 className="text-xl font-serif font-medium text-text-light mb-2">
                {matches.length === 0 ? 'No matches found' : `Found ${matches.length} ${matches.length === 1 ? 'photo' : 'photos'} of you`}
              </h2>
              {matches.length === 0 && (
                <p className="text-text-muted text-sm">Try a clearer selfie with better lighting. Nothing was kept from this search.</p>
              )}
              {shareToken && matches.length > 0 && (
                <div className="mt-4 p-3 bg-champagne/5 border border-champagne/20 rounded-lg">
                  <p className="text-xs text-text-muted mb-2">Save this private link to revisit your photos for 30 days:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-champagne flex-1 truncate">
                      {`${window.location.origin}/my-moments/${shareToken}`}
                    </code>
                    <button onClick={copyShareLink} className="p-2.5 text-champagne hover:bg-champagne/10 rounded min-w-[44px] min-h-[44px] flex items-center justify-center">
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              <div className="flex gap-3 mt-4">
                <button onClick={reset} className="btn-outline text-sm">Search Again</button>
                {shareToken && (
                  <button onClick={handleForgetMe} className="btn-outline text-sm flex items-center gap-1 text-red-400 border-red-400/30 hover:border-red-400">
                    <Trash2 className="w-4 h-4" /> Forget me
                  </button>
                )}
              </div>
            </div>

            {matches.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {matches.map(m => (
                  <a key={m.id} href={`${API_URL}${m.url}`} target="_blank" rel="noopener noreferrer" className="aura-card rounded-xl overflow-hidden block">
                    {m.file_type === 'video' ? (
                      <video src={`${API_URL}${m.url}`} className="w-full aspect-square object-cover" muted />
                    ) : (
                      <img src={`${API_URL}${m.url}`} alt={`By ${m.guest_name}`} className="w-full aspect-square object-cover" />
                    )}
                    <p className="text-xs text-text-muted p-2">By {m.guest_name}</p>
                  </a>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
      <FooterExpanded />
    </div>
  );
}
